import { Suspense, useEffect, lazy, ReactNode, ReactElement } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { SessionProvider, useSession } from './contexts/SessionContext'
import { ProModeProvider } from './contexts/ProModeContext'
import { CorporateFilterProvider } from './contexts/CorporateFilterContext'
import { ThemeProvider } from './components/theme-provider'
import { UserRoles, type UserRole } from './constants/userRoles'
import { ROUTES } from './constants/routes'
import {
  RouteErrorBoundary,
  ChunkLoadErrorBoundary,
} from './components/ErrorBoundary'
import {
  PageLoadingWithSidebar,
  FormLoading,
  DashboardLoading,
  TableLoading,
} from './components/ui/loading'
import { registerPreload, useRoutePreloader } from './hooks/useRoutePreloader'
import { Toaster } from './components/ui/sonner'
import { performanceMonitor } from './services/performanceMonitor'

// Auth components - keep these static for fast initial auth flow
import SignIn from './components/auth/SignIn'
import SignUp from './components/auth/SignUp'
import AcceptInvitation from './pages/AcceptInvitation'
import ConfirmPhone from './pages/ConfirmPhone'
import ForgotPassword from './components/auth/ForgotPassword'

// Home page - keep static for fast initial load
import Home from './pages/Home'
import { logger } from '@/services/logger'

// Lazy load all page components with named chunks for better caching
const Dashboard = lazy(() =>
  import('./pages/Dashboard' /* webpackChunkName: "dashboard" */).then(
    module => {
      registerPreload('dashboard', () => import('./pages/Dashboard'))
      return module
    }
  )
)

const MemberDetailsPage = lazy(() =>
  import('./pages/MemberDetails' /* webpackChunkName: "member-details" */).then(
    module => {
      registerPreload('memberDetails', () => import('./pages/MemberDetails'))
      return module
    }
  )
)

const ProfileCompletionPage = lazy(
  () =>
    import(
      './pages/ProfileCompletionPage' /* webpackChunkName: "profile-completion" */
    )
)

// Management pages - grouped for role-based access
const TeamPage = lazy(() =>
  import('./pages/Team' /* webpackChunkName: "team" */).then(module => {
    registerPreload('team', () => import('./pages/Team'))
    return module
  })
)

const PropertiesPage = lazy(() =>
  import('./pages/Properties' /* webpackChunkName: "properties" */).then(
    module => {
      registerPreload('properties', () => import('./pages/Properties'))
      return module
    }
  )
)

const AddProperty = lazy(
  () => import('./pages/AddProperty' /* webpackChunkName: "add-property" */)
)

const EditProperty = lazy(
  () => import('./pages/EditProperty' /* webpackChunkName: "edit-property" */)
)

const AddTenant = lazy(
  () => import('./pages/AddTenant' /* webpackChunkName: "add-tenant" */)
)

const PropertyDetails = lazy(
  () =>
    import('./pages/PropertyDetails' /* webpackChunkName: "property-details" */)
)

const AddEquipment = lazy(
  () => import('./pages/AddEquipment' /* webpackChunkName: "add-equipment" */)
)

const EquipmentDetails = lazy(
  () =>
    import(
      './pages/EquipmentDetails' /* webpackChunkName: "equipment-details" */
    )
)

// MaintenanceWizard removed — maintenance mode consolidated into EquipmentScanWizard (D32-D34)

const NotificationsPage = lazy(
  () => import('./pages/Notifications' /* webpackChunkName: "notifications" */)
)

const TenantMaintenancePage = lazy(
  () =>
    import(
      './pages/TenantMaintenance' /* webpackChunkName: "tenant-maintenance" */
    )
)

// Tenant Maintenance History — read-only past scans (Phase 1b, P1b.9)
const TenantMaintenanceHistoryPage = lazy(
  () =>
    import(
      './pages/TenantMaintenanceHistory' /* webpackChunkName: "tenant-maintenance-history" */
    )
)

// Maintenance Record — admin read surface (Phase 1b, P1b.5)
const MaintenanceRecordPage = lazy(
  () =>
    import(
      './pages/maintenance/MaintenanceRecord' /* webpackChunkName: "maintenance-record" */
    )
)
const MaintenanceReviewsPage = lazy(
  () =>
    import(
      './pages/maintenance/MaintenanceReviews' /* webpackChunkName: "maintenance-reviews" */
    )
)
const MaintenanceRecordPropertyPage = lazy(
  () =>
    import(
      './pages/maintenance/MaintenanceRecordProperty' /* webpackChunkName: "maintenance-record-property" */
    )
)
const MaintenanceRecordScanPage = lazy(
  () =>
    import(
      './pages/maintenance/MaintenanceRecordScan' /* webpackChunkName: "maintenance-record-scan" */
    )
)

// Tenants page - Management+ access
const TenantsPage = lazy(() =>
  import('./pages/Tenants' /* webpackChunkName: "tenants" */).then(module => {
    registerPreload('tenants', () => import('./pages/Tenants'))
    return module
  })
)

const TenantDetails = lazy(
  () => import('./pages/TenantDetails' /* webpackChunkName: "tenant-details" */)
)

const ContactUs = lazy(
  () => import('./pages/ContactUs' /* webpackChunkName: "contact-us" */)
)

// Corporate-only pages
const DevUserCreator = lazy(() =>
  import(
    './components/admin/DevUserCreator' /* webpackChunkName: "dev-user-creator" */
  ).then(module => {
    registerPreload(
      'devUserCreator',
      () => import('./components/admin/DevUserCreator')
    )
    return module
  })
)

// Performance Dashboard - Admin access
const PerformanceDashboard = lazy(
  () =>
    import(
      './components/admin/PerformanceDashboard' /* webpackChunkName: "performance-dashboard" */
    )
)

// Invitations - Management+ access
const InvitationsPage = lazy(
  () => import('./pages/Invitations' /* webpackChunkName: "invitations" */)
)

// Component prop interfaces
interface ProtectedRouteProps {
  children: ReactNode
  requiredRole?: UserRole
  loadingComponent?: ReactElement
}

interface RouteWithLoadingProps {
  children: ReactNode
  loadingComponent?: ReactElement
  routeName: string
}

// Protected route wrapper with enhanced loading states
const ProtectedRoute = ({
  children,
  requiredRole,
  loadingComponent,
}: ProtectedRouteProps): ReactElement => {
  const { currentUser, hasRole, loading, isInitialized } = useSession()

  // Wait for auth to be initialized before making routing decisions
  if (!isInitialized) {
    return loadingComponent || <PageLoadingWithSidebar />
  }

  // Auth is initialized - now check loading state (role fetch in progress)
  if (loading) {
    return loadingComponent || <PageLoadingWithSidebar />
  }

  // Auth checked and no user - redirect to signin
  if (!currentUser) {
    console.info('[AuthRestore] ProtectedRoute → /signin (no user)', {
      path: window.location.pathname,
    })
    return <Navigate to="/signin" />
  }

  // Check role permissions
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/dashboard" />
  }

  return <>{children}</>
}

// Specialized loading components for different route types
const RouteWithLoading = ({
  children,
  loadingComponent = <PageLoadingWithSidebar />,
  routeName,
}: RouteWithLoadingProps): ReactElement => (
  <RouteErrorBoundary routeName={routeName}>
    <Suspense fallback={loadingComponent}>{children}</Suspense>
  </RouteErrorBoundary>
)

// Main App component with route preloader
const AppContent = (): ReactElement => {
  useRoutePreloader() // Preload critical routes based on user role

  // Performance monitoring is automatically initialized by the service constructor
  useEffect(() => {
    // Log development mode status
    if (
      import.meta.env.DEV &&
      import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true'
    ) {
      logger.debug('🔧 Performance monitoring enabled (development mode)')
    }

    // Cleanup on unmount
    return () => {
      if (typeof window !== 'undefined') {
        performanceMonitor.disable()
      }
    }
  }, [])

  return (
    <Routes>
      {/* Full-screen routes (auth and home) - no loading wrapper for static components */}
      <Route path="/" element={<Home />} />
      <Route path="/signin" element={<SignIn redirectPath="/dashboard" />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      {/* Admin's phone step after accepting an invitation (PB-88) */}
      <Route
        path="/confirm-phone"
        element={
          <ProtectedRoute loadingComponent={<FormLoading />}>
            <ConfirmPhone />
          </ProtectedRoute>
        }
      />

      {/* Protected routes with appropriate loading components */}
      <Route
        path="/user-details/complete"
        element={
          <ProtectedRoute loadingComponent={<FormLoading />}>
            <RouteWithLoading
              routeName="user-details-completion"
              loadingComponent={<FormLoading />}
            >
              <ProfileCompletionPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      <Route
        path="/team/:id"
        element={
          <ProtectedRoute loadingComponent={<PageLoadingWithSidebar />}>
            <RouteWithLoading
              routeName="member-details"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <MemberDetailsPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      <Route
        path="/pro-dashboard"
        element={
          <ProtectedRoute loadingComponent={<DashboardLoading />}>
            <RouteWithLoading
              routeName="pro-dashboard"
              loadingComponent={<DashboardLoading />}
            >
              {/* Redirects to dashboard — standalone pro-dashboard page removed */}
              <Dashboard />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute loadingComponent={<DashboardLoading />}>
            <RouteWithLoading
              routeName="dashboard"
              loadingComponent={<DashboardLoading />}
            >
              <Dashboard />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.MY_MAINTENANCE}
        element={
          <ProtectedRoute loadingComponent={<PageLoadingWithSidebar />}>
            <RouteWithLoading
              routeName="my-maintenance"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <TenantMaintenancePage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Tenant Maintenance History — read-only past scans (Phase 1b, P1b.9).
          Nav + deep-links point here at the P1b.11 cutover; reachable by URL now. */}
      <Route
        path={ROUTES.MAINTENANCE_HISTORY}
        element={
          <ProtectedRoute loadingComponent={<PageLoadingWithSidebar />}>
            <RouteWithLoading
              routeName="maintenance-history"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <TenantMaintenanceHistoryPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Contact MNTND support (DU-004 — request a tenant move) */}
      <Route
        path={ROUTES.CONTACT}
        element={
          <ProtectedRoute loadingComponent={<PageLoadingWithSidebar />}>
            <RouteWithLoading
              routeName="contact-us"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <ContactUs />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />
      {/* Maintenance Record — admin read surface (Phase 1b, P1b.5) */}
      <Route
        path={ROUTES.MAINTENANCE_RECORD}
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<TableLoading />}
          >
            <RouteWithLoading
              routeName="maintenance-record"
              loadingComponent={<TableLoading />}
            >
              <MaintenanceRecordPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />
      {/* Full review worklist — must precede the `:propertyId` route so
          "reviews" isn't captured as a property id (D36). */}
      <Route
        path={ROUTES.MAINTENANCE_REVIEWS}
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<TableLoading />}
          >
            <RouteWithLoading
              routeName="maintenance-reviews"
              loadingComponent={<TableLoading />}
            >
              <MaintenanceReviewsPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.MAINTENANCE_RECORD}/:propertyId`}
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="maintenance-record-property"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <MaintenanceRecordPropertyPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.MAINTENANCE_RECORD}/:propertyId/:submissionId`}
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="maintenance-record-scan"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <MaintenanceRecordScanPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Admin routes - Allow MANAGEMENT+ access */}
      <Route
        path="/team"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<TableLoading />}
          >
            <RouteWithLoading
              routeName="team"
              loadingComponent={<TableLoading />}
            >
              <TeamPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      <Route
        path="/properties"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<TableLoading />}
          >
            <RouteWithLoading
              routeName="properties"
              loadingComponent={<TableLoading />}
            >
              <PropertiesPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      <Route
        path="/properties/add"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<FormLoading />}
          >
            <RouteWithLoading
              routeName="add-property"
              loadingComponent={<FormLoading />}
            >
              <AddProperty />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      <Route
        path="/properties/:id"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="property-details"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <PropertyDetails />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Edit Property - Admin access */}
      <Route
        path="/properties/:id/edit"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.ADMIN}
            loadingComponent={<FormLoading />}
          >
            <RouteWithLoading
              routeName="edit-property"
              loadingComponent={<FormLoading />}
            >
              <EditProperty />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Edit Equipment - Management+ access */}
      <Route
        path="/properties/:id/equipment/:equipmentId/edit"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="edit-equipment"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <AddEquipment />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Equipment Details - Management+ access (read-only view) */}
      <Route
        path="/properties/:id/equipment/:equipmentId"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="equipment-details"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <EquipmentDetails />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Maintenance wizard routes removed — consolidated into EquipmentScanWizard dialog (D32-D37) */}

      <Route
        path="/notifications"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.TENANT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="notifications"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <NotificationsPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Tenants - Management+ access */}
      <Route
        path="/tenants"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<TableLoading />}
          >
            <RouteWithLoading
              routeName="tenants"
              loadingComponent={<TableLoading />}
            >
              <TenantsPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Add Tenant - Management+ access */}
      <Route
        path="/tenants/add"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="add-tenant"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <AddTenant />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Tenant Detail - Management+ access */}
      <Route
        path="/tenants/:id"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="tenant-details"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <TenantDetails />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Performance Dashboard - Admin access required */}
      <Route
        path="/admin/performance"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.ADMIN}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="performance-dashboard"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <PerformanceDashboard />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Invitations - Management+ access required */}
      <Route
        path="/admin/invitations"
        element={
          <ProtectedRoute
            requiredRole={UserRoles.MANAGEMENT}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="invitations"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <InvitationsPage />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Dev Tools - Corporate access only. The old /admin/seed-data path
          redirects so existing bookmarks keep working. */}
      <Route
        path="/admin/seed-data"
        element={<Navigate to={ROUTES.DEV_TOOLS} replace />}
      />
      <Route
        path={ROUTES.DEV_TOOLS}
        element={
          <ProtectedRoute
            requiredRole={UserRoles.CORPORATE}
            loadingComponent={<PageLoadingWithSidebar />}
          >
            <RouteWithLoading
              routeName="dev-user-creator"
              loadingComponent={<PageLoadingWithSidebar />}
            >
              <DevUserCreator />
            </RouteWithLoading>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect based on auth status */}
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  )
}

// Catch-all redirect component
const CatchAllRedirect = (): ReactElement => {
  const { isAuthenticated } = useSession()
  return <Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />
}

export default function App(): ReactElement {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <ChunkLoadErrorBoundary>
        <SessionProvider>
          <ProModeProvider>
            <CorporateFilterProvider>
              <AppContent />
              <Toaster />
            </CorporateFilterProvider>
          </ProModeProvider>
        </SessionProvider>
      </ChunkLoadErrorBoundary>
    </ThemeProvider>
  )
}
